/**
 * Shared pricing calculator — used both by the admin editor (preview) and the
 * public landing page. Keep this file DUMB (no fetch, no side effects) so
 * the exact same computation runs on both sides and prices always match.
 *
 * Data model (property.pricing):
 * {
 *   enabled: boolean,
 *   base_price: number | null,      // EUR, whole units
 *   currency: "EUR",
 *   weekend_enabled: boolean,
 *   weekend_uplift_pct: number,     // 0-100, applied to Fri + Sat
 *   monthly_enabled: boolean,
 *   monthly_modifiers: { 1..12: pct }, // negative = discount
 *   promo_days: [
 *     { date: "YYYY-MM-DD", discount_pct: number, note?: string }
 *   ]
 * }
 *
 * Hierarchy (applied in order): base_price
 *   × (1 + monthly_modifier / 100)
 *   × (1 + weekend_uplift / 100)      // only Fri & Sat
 *   × (1 - promo_discount / 100)      // only listed promo days
 */

export const DEFAULT_PRICING = () => ({
  enabled: false,
  base_price: null,
  currency: "EUR",
  // Number of guests the base_price refers to. Shown in landing so the user
  // knows "€90 a partire da, per 2 persone".
  base_guests: null,
  // Minimum nights required for a booking. When null / 0 → not shown in landing.
  min_nights: null,
  // Optional fees. When left null / 0, the landing hides the corresponding line.
  cleaning_fee: null,       // one-off flat fee (€)
  tourist_tax_per_person: null, // per person per night (€)
  // Extra supplements (repeatable). Each: { id, type, label, amount, unit }
  //   type: one of EXTRA_FEE_TYPES keys ('altro' → custom label)
  //   unit: 'stay' (a soggiorno) | 'night' (a notte)
  extra_fees: [],
  weekend_enabled: false,
  weekend_uplift_pct: 20,
  monthly_enabled: false,
  monthly_modifiers: { 1:0, 2:0, 3:0, 4:0, 5:0, 6:0, 7:0, 8:0, 9:0, 10:0, 11:0, 12:0 },
  promo_days: [],
  // date_overrides: absolute per-day price set from the host calendar
  // Airbnb-style. Highest priority — bypasses every rule. { "YYYY-MM-DD": 120 }
  date_overrides: {},
});

/**
 * @param {object} pricing        — property.pricing object
 * @param {string} dateStr        — YYYY-MM-DD
 * @returns {null | { price:number, promo:boolean, discount_pct?:number, note?:string, has_weekend:boolean, monthly_pct:number }}
 */
export const computePriceForDate = (pricing, dateStr) => {
  if (!pricing?.enabled || !pricing.base_price || pricing.base_price <= 0) return null;
  if (!dateStr || typeof dateStr !== "string") return null;

  // 0) Per-day absolute override wins over everything else — that's the
  //    Airbnb-style manual price the host set from the calendar.
  const override = pricing.date_overrides?.[dateStr];
  if (override != null && Number(override) > 0) {
    return {
      price: Math.round(Number(override)),
      promo: false,
      override: true,
      has_weekend: false,
      monthly_pct: 0,
    };
  }

  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  const month = date.getMonth() + 1;      // 1..12
  const dow = date.getDay();              // 0=sun ... 5=fri, 6=sat
  const isWeekend = dow === 5 || dow === 6;

  let price = Number(pricing.base_price);

  // 1) Monthly modifier
  const monthlyPct = Number(pricing.monthly_modifiers?.[month] || 0);
  if (pricing.monthly_enabled && monthlyPct !== 0) {
    price *= 1 + monthlyPct / 100;
  }

  // 2) Weekend uplift compounds ON TOP of monthly
  const weekendPct = Number(pricing.weekend_uplift_pct || 0);
  if (pricing.weekend_enabled && isWeekend && weekendPct !== 0) {
    price *= 1 + weekendPct / 100;
  }

  // 3) Promo discount override
  const promo = (pricing.promo_days || []).find((p) => p.date === dateStr);
  if (promo) {
    const disc = Number(promo.discount_pct || 0);
    price *= 1 - disc / 100;
    return {
      price: Math.max(0, Math.round(price)),
      promo: true,
      discount_pct: disc,
      note: promo.note || "",
      has_weekend: isWeekend && pricing.weekend_enabled,
      monthly_pct: pricing.monthly_enabled ? monthlyPct : 0,
    };
  }

  return {
    price: Math.max(0, Math.round(price)),
    promo: false,
    has_weekend: isWeekend && pricing.weekend_enabled,
    monthly_pct: pricing.monthly_enabled ? monthlyPct : 0,
  };
};

export const formatPrice = (n) =>
  n == null ? "—" : `€${Math.round(n).toLocaleString("it-IT")}`;

export const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];


// Catalogo tipi di supplemento (voce "Altri supplementi" nei prezzi).
export const EXTRA_FEE_TYPES = [
  { key: "animali", label: "Animali", emoji: "🐾" },
  { key: "culla", label: "Culla", emoji: "🍼" },
  { key: "letto_aggiuntivo", label: "Letto aggiuntivo", emoji: "🛏️" },
  { key: "cambio_biancheria", label: "Cambio biancheria", emoji: "🧺" },
  { key: "pulizia_aggiuntiva", label: "Pulizia aggiuntiva", emoji: "🧽" },
  { key: "spa", label: "SPA", emoji: "🧖" },
  { key: "piscina", label: "Piscina", emoji: "🏊" },
  { key: "legna_camino", label: "Legna camino", emoji: "🔥" },
  { key: "altro", label: "Altro", emoji: "➕" },
];

// Etichette supplementi tradotte (set fisso). 'altro' usa il testo libero host.
const EXTRA_FEE_I18N = {
  animali: { it: "Animali", en: "Pets", fr: "Animaux", es: "Mascotas", de: "Haustiere" },
  culla: { it: "Culla", en: "Cot", fr: "Lit bébé", es: "Cuna", de: "Babybett" },
  letto_aggiuntivo: { it: "Letto aggiuntivo", en: "Extra bed", fr: "Lit supplémentaire", es: "Cama adicional", de: "Zustellbett" },
  cambio_biancheria: { it: "Cambio biancheria", en: "Linen change", fr: "Change de linge", es: "Cambio de ropa", de: "Wäschewechsel" },
  pulizia_aggiuntiva: { it: "Pulizia aggiuntiva", en: "Extra cleaning", fr: "Ménage supplémentaire", es: "Limpieza adicional", de: "Zusatzreinigung" },
  spa: { it: "SPA", en: "SPA", fr: "SPA", es: "SPA", de: "SPA" },
  piscina: { it: "Piscina", en: "Pool", fr: "Piscine", es: "Piscina", de: "Pool" },
  legna_camino: { it: "Legna camino", en: "Firewood", fr: "Bois de cheminée", es: "Leña", de: "Brennholz" },
  altro: { it: "Altro", en: "Other", fr: "Autre", es: "Otro", de: "Sonstige" },
};
const SUPPL_FALLBACK = { it: "Supplemento", en: "Surcharge", fr: "Supplément", es: "Suplemento", de: "Zuschlag" };

// Risolve emoji + etichetta di un supplemento (gestisce il tipo 'altro' custom).
// `lang` opzionale per tradurre le etichette del set fisso.
export const resolveExtraFee = (item, lang = "it") => {
  const def = EXTRA_FEE_TYPES.find((t) => t.key === item?.type);
  if (item?.type === "altro" || !def) {
    return { emoji: "➕", label: (item?.label || "").trim() || (SUPPL_FALLBACK[lang] || SUPPL_FALLBACK.it) };
  }
  const tr = EXTRA_FEE_I18N[def.key];
  return { emoji: def.emoji, label: (tr && (tr[lang] || tr.it)) || def.label };
};
